/**
 * 게시글 상세 + 댓글/좋아요/채택.
 *
 * 영구지침: docs/develop/community/post.md §7.2
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import ImageLightbox from './components/ImageLightbox'
import {
  Heart,
  MessageCircle,
  Eye,
  ChevronRight,
  Trash2,
  Pencil,
  Check,
  Send,
  Lock,
  List,
  Megaphone,
  Globe,
  Paperclip,
  Download,
} from 'lucide-react'
import { API_BASE_URL } from '@/lib/constants'
import SEO from '@/components/common/SEO'
import StickySideBanner from '@/components/common/StickySideBanner'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'
import {
  useAcceptAnswer,
  useCreateComment,
  useDeleteComment,
  useDeletePost,
  usePost,
  usePostComments,
  useToggleLike,
  type CommentItem,
} from '@/features/community/publicApi'

export default function PostDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = idParam ? Number(idParam) : null
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()

  const { data: post, isLoading } = usePost(id)
  const { data: comments = [] } = usePostComments(id)

  const likeMut = useToggleLike()
  const deletePostMut = useDeletePost()
  const createCommentMut = useCreateComment(id ?? 0)
  const deleteCommentMut = useDeleteComment()
  const acceptMut = useAcceptAnswer(id ?? 0)

  const [commentContent, setCommentContent] = useState('')
  const [commentAnonymous, setCommentAnonymous] = useState(false)
  const [replyParentId, setReplyParentId] = useState<number | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const imageAttachments = useMemo(
    () => (post?.attachments ?? []).filter((a) => a.kind === 'IMAGE'),
    [post?.attachments],
  )

  if (isLoading || !post || id == null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const isPostOwner = !!user && post.author.id === user.id
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
  const canAcceptAnswer =
    !!comments.length && (isPostOwner || isAdmin) && post.acceptedAnswerId == null

  const handleLike = () => {
    if (!isAuthenticated) {
      toast.info('로그인이 필요합니다')
      navigate('/login')
      return
    }
    likeMut.mutate(id)
  }

  const handleDeletePost = () => {
    if (!confirm('게시글을 삭제하시겠습니까?')) return
    deletePostMut.mutate(id, {
      onSuccess: () => {
        toast.success('삭제되었습니다')
        navigate(`/community/${post.board.slug}`)
      },
      onError: (err: unknown) => toast.error(extractErrorMessage(err) ?? '삭제 실패'),
    })
  }

  const handleSubmitComment = () => {
    if (!isAuthenticated) {
      toast.info('로그인이 필요합니다')
      return
    }
    if (!commentContent.trim()) return
    createCommentMut.mutate(
      {
        content: commentContent,
        parentId: replyParentId,
        isAnonymous: commentAnonymous,
      },
      {
        onSuccess: () => {
          setCommentContent('')
          setReplyParentId(null)
          setCommentAnonymous(false)
          toast.success('댓글이 등록되었습니다')
        },
        onError: (err: unknown) => toast.error(extractErrorMessage(err) ?? '등록 실패'),
      },
    )
  }

  const handleDeleteComment = (commentId: number) => {
    if (!confirm('댓글을 삭제하시겠습니까?')) return
    deleteCommentMut.mutate(commentId, {
      onError: (err: unknown) => toast.error(extractErrorMessage(err) ?? '삭제 실패'),
    })
  }

  const handleAccept = (commentId: number) => {
    if (!confirm('이 답변을 채택하시겠습니까?')) return
    acceptMut.mutate(commentId, {
      onSuccess: () => toast.success('답변이 채택되었습니다'),
      onError: (err: unknown) => toast.error(extractErrorMessage(err) ?? '채택 실패'),
    })
  }

  // SEO description — 본문 HTML 태그 제거 후 발췌 (숨김/비밀 글은 색인 메타 생략)
  const plainExcerpt = post.content
    ? post.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 150)
    : `${post.board.name}의 글입니다.`
  // 숨김/비밀 글은 SEO 메타 자체를 생략. 외부 자동수집(KOGL 공공저작물 전재) 글은
  // 메타는 두되 noindex로 색인만 제외한다(중복 콘텐츠 회피 — 애드센스/SEO 품질).
  const seoVisible = !post.isHidden && !post.isSecret
  const isSyndicated = !!post.isSyndicated

  return (
    <>
      {/* 게시판 상세 좌우 세로배너(160x600) — 콘텐츠(max-w-3xl=768) 바로 옆 여백에.
          초광폭(xl=1280px↑)에서만, 푸터 위에서 멈춤(StickySideBanner). */}
      <StickySideBanner slotKey="community_post_side_left" side="left" topPx={96} edgeOffsetPx={592} breakpoint="xl" />
      <StickySideBanner slotKey="community_post_side_right" side="right" topPx={96} edgeOffsetPx={592} breakpoint="xl" />
      {/* 짧은 글이면 푸터가 올라와 600px 세로배너 자리가 없어진다. BoardPage 와 같은 기준 */}
      <main className="mx-auto min-h-[712px] w-full max-w-3xl px-4 py-6">
      {seoVisible && (
        <SEO
          title={`${post.title} | ${post.board.name}`}
          description={plainExcerpt}
          url={`/community/posts/${id}`}
          noindex={isSyndicated}
        />
      )}
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4 flex items-center gap-1 text-caption text-cb-gray-500">
        <Link to="/community" className="hover:text-cb-gray-900">
          커뮤니티
        </Link>
        <ChevronRight size={12} className="text-cb-gray-300" />
        <Link to={`/community/${post.board.slug}`} className="hover:text-cb-gray-900">
          {post.board.name}
        </Link>
        <ChevronRight size={12} className="text-cb-gray-300" />
        <span className="truncate text-cb-gray-700">상세</span>
      </nav>

      {post.isHidden && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-body-sm text-red-700">
          <Lock size={16} /> 이 게시글은 관리자에 의해 숨김 처리되었습니다.
        </div>
      )}

      {post.isSecret && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2 text-body-sm text-amber-800">
          <Lock size={16} /> 비밀글 — {post.editableByMe
            ? '작성자 본인과 운영자만 볼 수 있습니다.'
            : '본인과 운영자만 본문·댓글을 볼 수 있습니다.'}
        </div>
      )}

      {/* 헤더 */}
      <header className="border-b border-cb-gray-200 pb-4">
        {(post.isGlobalNotice || post.isNotice) && (
          <div className="mb-2 flex items-center gap-1.5">
            {post.isGlobalNotice ? (
              <span className="inline-flex items-center gap-1 rounded bg-cb-primary px-2 py-0.5 text-caption font-medium text-white">
                <Globe size={11} /> 전체공지
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-cb-primary-light px-2 py-0.5 text-caption font-medium text-cb-primary">
                <Megaphone size={11} /> 공지
              </span>
            )}
          </div>
        )}
        <h1 className="text-heading-md font-bold text-cb-gray-900">{post.title}</h1>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-caption text-cb-gray-500">
            <span className="font-medium text-cb-gray-700">{post.author.nickname}</span>
            <span>·</span>
            <span>{new Date(post.createdAt).toLocaleString('ko-KR')}</span>
          </div>
          <div className="flex items-center gap-3 text-caption text-cb-gray-400">
            <span className="inline-flex items-center gap-1">
              <Eye size={12} /> {post.viewCount}
            </span>
            <span className="inline-flex items-center gap-1">
              <Heart size={12} /> {post.likeCount}
            </span>
          </div>
        </div>
      </header>

      {/* 본문 — 외부 자동 수집글(html)은 백엔드 OWASP Sanitizer + DOMPurify 이중 정화,
          일반 글(text/마크다운)은 renderMarkdown 으로 변환.
          영구지침: docs/develop/community/info-board-auto-import.md §6 / AGENTS.md §보안 4 */}
      <article
        className="prose prose-sm max-w-none py-6 text-cb-gray-800 break-words [overflow-wrap:anywhere]"
        dangerouslySetInnerHTML={{
          __html:
            post.contentFormat === 'html'
              ? sanitizeHtml(post.content)
              : renderMarkdown(post.content),
        }}
      />

      {/* 첨부 — 이미지 갤러리 (클릭 시 라이트박스) */}
      {imageAttachments.length > 0 && (
        <section className="mb-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {imageAttachments.map((a, i) => (
              <button
                key={a.fileKey}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="group block overflow-hidden rounded-lg border border-cb-gray-200 bg-cb-gray-50 focus:outline-none focus:ring-2 focus:ring-cb-primary"
                title={a.originalName}
                aria-label={`이미지 보기: ${a.originalName}`}
              >
                <img
                  src={`${API_BASE_URL}/files/${a.fileKey}`}
                  alt={a.originalName}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 이미지 라이트박스 */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={imageAttachments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* 첨부 — 파일 다운로드 리스트 */}
      {post.attachments && post.attachments.filter((a) => a.kind === 'FILE').length > 0 && (
        <section className="mb-4 rounded-xl border border-cb-gray-200 bg-white p-3">
          <h3 className="mb-2 flex items-center gap-1.5 text-caption font-medium text-cb-gray-700">
            <Paperclip size={12} /> 첨부 파일
          </h3>
          <ul className="space-y-1.5">
            {post.attachments
              .filter((a) => a.kind === 'FILE')
              .map((a) => (
                <li key={a.fileKey}>
                  <a
                    href={`${API_BASE_URL}/files/${a.fileKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={a.originalName}
                    className="inline-flex items-center gap-2 rounded bg-cb-gray-50 px-3 py-1.5 text-caption text-cb-gray-800 hover:bg-cb-gray-100"
                  >
                    <Download size={12} />
                    {a.originalName}
                    <span className="text-cb-gray-400">({formatBytes(a.sizeBytes)})</span>
                  </a>
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* 액션 버튼 */}
      <div className="flex items-center justify-between border-t border-cb-gray-200 pt-4">
        <button
          type="button"
          onClick={handleLike}
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-body-sm transition-colors ${
            post.likedByMe
              ? 'border-red-200 bg-red-50 text-red-600'
              : 'border-cb-gray-200 text-cb-gray-500 hover:border-cb-gray-300'
          }`}
        >
          <Heart size={14} fill={post.likedByMe ? 'currentColor' : 'none'} />
          좋아요 {post.likeCount}
        </button>
        <div className="flex gap-2">
          <Link
            to={`/community/${post.board.slug}`}
            className="inline-flex items-center gap-1 rounded border border-cb-gray-200 px-3 py-1.5 text-caption text-cb-gray-600 hover:bg-cb-gray-50"
          >
            <List size={12} /> 목록
          </Link>
          {post.editableByMe && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/community/posts/${post.id}/edit`)}
                className="inline-flex items-center gap-1 rounded border border-cb-gray-200 px-3 py-1.5 text-caption text-cb-gray-600 hover:bg-cb-gray-50"
              >
                <Pencil size={12} /> 수정
              </button>
              <button
                type="button"
                onClick={handleDeletePost}
                className="inline-flex items-center gap-1 rounded border border-red-200 px-3 py-1.5 text-caption text-red-600 hover:bg-red-50"
              >
                <Trash2 size={12} /> 삭제
              </button>
            </>
          )}
        </div>
      </div>

      {/* 내부링크 — 커뮤니티 글 → 핵심 서비스 페이지 (SEO 크롤 경로) */}
      <section className="mt-8 rounded-2xl border border-cb-gray-200 bg-cb-gray-50 p-5" aria-label="케어밸런스 더 알아보기">
        <h2 className="mb-3 text-body-md font-bold text-cb-gray-900">케어밸런스에서 더 알아보기</h2>
        <div className="flex flex-wrap gap-2">
          <Link to="/facilities" className="rounded-full border border-cb-gray-200 bg-white px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary">
            요양시설 검색·비교
          </Link>
          <Link to="/cost-simulator" className="rounded-full border border-cb-gray-200 bg-white px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary">
            비용 시뮬레이터
          </Link>
          <Link to="/blog" className="rounded-full border border-cb-gray-200 bg-white px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary">
            돌봄 정보 블로그
          </Link>
          <Link to={`/community/${post.board.slug}`} className="rounded-full border border-cb-gray-200 bg-white px-4 py-2 text-sm text-cb-gray-700 hover:border-cb-primary hover:text-cb-primary">
            {post.board.name} 더 보기
          </Link>
        </div>
      </section>

      {/* 댓글 섹션 */}
      <section className="mt-8">
        <h2 className="mb-4 text-body-md font-semibold text-cb-gray-900">
          댓글 {post.commentCount}
        </h2>

        {/* 채택된 답변 먼저 */}
        {post.acceptedAnswerId != null && (
          (() => {
            const accepted = comments.find((c) => c.id === post.acceptedAnswerId)
            if (!accepted) return null
            return (
              <div className="mb-4 rounded-xl border-2 border-green-400 bg-green-50 p-4">
                <div className="mb-1 inline-flex items-center gap-1 text-caption font-medium text-green-700">
                  <Check size={12} /> 채택된 답변
                </div>
                <CommentRow
                  c={accepted}
                  postAcceptedId={post.acceptedAnswerId}
                  canAccept={false}
                  onDelete={handleDeleteComment}
                  onReply={() => setReplyParentId(accepted.id)}
                  onAccept={() => void 0}
                />
              </div>
            )
          })()
        )}

        <ul className="space-y-3">
          {comments
            .filter((c) => post.acceptedAnswerId == null || c.id !== post.acceptedAnswerId)
            .map((c) => (
              <li
                key={c.id}
                className={`rounded-xl border border-cb-gray-200 p-3 ${
                  c.parentId != null ? 'ml-6 bg-cb-gray-50/40 sm:ml-10' : ''
                }`}
              >
                <CommentRow
                  c={c}
                  postAcceptedId={post.acceptedAnswerId}
                  canAccept={canAcceptAnswer}
                  onDelete={handleDeleteComment}
                  onReply={() => setReplyParentId(c.id)}
                  onAccept={() => handleAccept(c.id)}
                />
              </li>
            ))}
        </ul>

        {/* 댓글 입력 */}
        {post.board && (
          <div className="mt-6 rounded-xl border border-cb-gray-200 bg-white p-4">
            {replyParentId != null && (
              <div className="mb-2 flex items-center justify-between rounded bg-cb-gray-50 px-3 py-1.5 text-caption text-cb-gray-600">
                답글 작성 중
                <button
                  type="button"
                  onClick={() => setReplyParentId(null)}
                  className="text-cb-gray-400 hover:text-cb-gray-600"
                >
                  취소
                </button>
              </div>
            )}
            <textarea
              placeholder="댓글을 입력하세요"
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-cb-gray-200 px-3 py-2 text-body-sm focus:border-cb-primary focus:outline-none"
            />
            <div className="mt-2 flex items-center justify-between">
              <label className="inline-flex items-center gap-1 text-caption text-cb-gray-600">
                <input
                  type="checkbox"
                  checked={commentAnonymous}
                  onChange={(e) => setCommentAnonymous(e.target.checked)}
                  disabled={!isAnonymousAllowed(post.board.slug, comments)}
                  className="h-3.5 w-3.5 accent-cb-primary"
                />
                익명
              </label>
              <Button size="sm" onClick={handleSubmitComment} disabled={createCommentMut.isPending}>
                <Send size={14} className="mr-1" />
                {createCommentMut.isPending ? '등록 중...' : '등록'}
              </Button>
            </div>
          </div>
        )}
      </section>
      </main>
    </>
  )
}

function CommentRow({
  c,
  postAcceptedId,
  canAccept,
  onDelete,
  onReply,
  onAccept,
}: {
  c: CommentItem
  postAcceptedId: number | null
  canAccept: boolean
  onDelete: (id: number) => void
  onReply: () => void
  onAccept: () => void
}) {
  const isReply = c.parentId != null
  return (
    <div className="flex gap-2">
      {isReply && (
        <span
          aria-hidden="true"
          className="mt-0.5 shrink-0 select-none text-cb-primary"
          title="답글"
        >
          ↳
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2 text-caption">
            <span className="font-medium text-cb-gray-700">{c.author.nickname}</span>
            <span className="text-cb-gray-400">·</span>
            <span className="text-cb-gray-400">{new Date(c.createdAt).toLocaleString('ko-KR')}</span>
            {c.isHidden && <span className="text-red-600">숨김</span>}
          </div>
          <div className="flex items-center gap-1">
            {canAccept && postAcceptedId == null && (
              <button
                type="button"
                onClick={onAccept}
                className="rounded bg-green-50 px-2 py-0.5 text-caption text-green-700 hover:bg-green-100"
              >
                채택
              </button>
            )}
            <button
              type="button"
              onClick={onReply}
              className="rounded px-2 py-0.5 text-caption text-cb-gray-500 hover:bg-cb-gray-100"
            >
              답글
            </button>
            {c.editableByMe && (
              <button
                type="button"
                onClick={() => onDelete(c.id)}
                className="rounded px-2 py-0.5 text-caption text-red-600 hover:bg-red-50"
              >
                삭제
              </button>
            )}
          </div>
        </div>
        <p className="whitespace-pre-wrap text-body-sm text-cb-gray-800">{c.content}</p>
      </div>
      <MessageCircle className="hidden" size={0} />
    </div>
  )
}

/**
 * 가벼운 마크다운 렌더러 — 서버에서 Sanitize 된 안전한 문자열을 전제.
 * 지원:
 *   - `# / ## / ###` 헤더
 *   - `**bold**`, `*italic*`
 *   - `- 아이템`, `* 아이템` (순서 없는 리스트)
 *   - `1. 아이템` (순서 있는 리스트)
 *   - 줄바꿈 보존 (`\n` → `<br/>`, 리스트/헤더 제외)
 */
/**
 * 외부 자동 수집글(html) 본문 살균.
 *
 * 백엔드에서 OWASP Java HTML Sanitizer(Jsoup Safelist.relaxed)로 이미 정화된 HTML 이지만,
 * dangerouslySetInnerHTML 사용 시 DOMPurify 로 한 번 더 살균(AGENTS.md §보안 4).
 * isply 본문 태그(<p> <h2> <h3> <ul> <li> <strong> <blockquote> <img>) + <a> 만 허용.
 * 영구지침: docs/develop/community/info-board-auto-import.md §6 / §7
 */
function sanitizeHtml(html: string): string {
  if (!html) return ''
  // SSR(Node)에는 DOM 이 없어 DOMPurify 가 동작하지 않는다(렌더 크래시 → 빈 본문).
  // 백엔드 OWASP Java HTML Sanitizer 로 이미 정화된 HTML 이므로 SSR 에서는 그대로 사용하고,
  // 클라이언트 하이드레이션 시 DOMPurify 로 한 번 더 살균한다(방어적, 동일 allowlist → 불일치 없음).
  if (typeof window === 'undefined') return html
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'em',
                   'b', 'i', 'br', 'a', 'blockquote', 'img', 'hr', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'loading', 'width', 'height'],
    ALLOWED_URI_REGEXP: /^(?:https?:|\/api\/v1\/files\/|\/[a-z0-9])/i,
  })
}

function renderMarkdown(md: string): string {
  if (!md) return ''
  // HTML 특수문자 이스케이프
  const escaped = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const lines = escaped.split(/\r?\n/)
  const out: string[] = []
  let listType: 'ul' | 'ol' | null = null

  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`)
      listType = null
    }
  }

  for (const rawLine of lines) {
    const line = rawLine
    // 리스트 (-, *, + 또는 1. 2. ...)
    const ulMatch = /^\s*[-*+]\s+(.+)$/.exec(line)
    const olMatch = /^\s*\d+\.\s+(.+)$/.exec(line)

    if (ulMatch) {
      if (listType !== 'ul') {
        closeList()
        out.push('<ul>')
        listType = 'ul'
      }
      out.push(`<li>${inline(ulMatch[1] ?? '')}</li>`)
      continue
    }
    if (olMatch) {
      if (listType !== 'ol') {
        closeList()
        out.push('<ol>')
        listType = 'ol'
      }
      out.push(`<li>${inline(olMatch[1] ?? '')}</li>`)
      continue
    }

    // 리스트 아닌 라인 — 지금까지 열려 있던 리스트 닫기
    closeList()

    // 헤더
    const h3 = /^### (.+)$/.exec(line)
    const h2 = /^## (.+)$/.exec(line)
    const h1 = /^# (.+)$/.exec(line)
    if (h3) { out.push(`<h3>${inline(h3[1] ?? '')}</h3>`); continue }
    if (h2) { out.push(`<h2>${inline(h2[1] ?? '')}</h2>`); continue }
    if (h1) { out.push(`<h1>${inline(h1[1] ?? '')}</h1>`); continue }

    // 빈 줄 → 빈 문단 유지
    if (line.trim() === '') {
      out.push('<br/>')
      continue
    }

    // 일반 텍스트 — <br/> 로 줄바꿈
    out.push(`${inline(line)}<br/>`)
  }
  closeList()

  return out.join('\n')
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** 인라인 스타일(bold/italic) 처리. */
function inline(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
}

function isAnonymousAllowed(_slug: string, _comments: CommentItem[]): boolean {
  // 간단 버전 — 게시판 설정은 상세 응답에 없고 별도 조회 필요.
  // Phase 2 에서는 전체 익명 허용 (서버가 최종 검증).
  return true
}

function extractErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err == null) return null
  const anyErr = err as { response?: { data?: { error?: { message?: string } } } }
  return anyErr.response?.data?.error?.message ?? null
}
